import React, { useEffect, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import QuickEntryDialog from "@/components/QuickEntryDialog";
import OcrEntryDialog from "@/components/OcrEntryDialog";
import SubscribeDialog from "@/components/SubscribeDialog";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { formatCurrency, formatDate, monthLabel } from "@/lib/format";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Trash2, Search, TrendingUp, TrendingDown, FileDown, FileText, ScanLine, Sparkles, Mic } from "lucide-react";
import { toast } from "sonner";
import { exportTransactionsCSV, exportTransactionsPDF } from "@/lib/export";

export default function Transactions() {
  const { isPremium } = useAuth();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ocrOpen, setOcrOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  // Allow other pages to open OCR dialog programmatically via window event
  useEffect(() => {
    const handler = () => setOcrOpen(true);
    window.addEventListener("finly:open-ocr", handler);
    return () => window.removeEventListener("finly:open-ocr", handler);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ month: String(month), year: String(year) });
      if (typeFilter !== "all") params.append("type", typeFilter);
      const [t, c, a] = await Promise.all([
        api.get(`/transactions?${params.toString()}`),
        api.get(`/categories`),
        api.get(`/accounts`),
      ]);
      setTransactions(t.data);
      setCategories(c.data);
      setAccounts(a.data || []);
    } catch (err) {
      if (err?.response?.status !== 401) {
        toast.error("No se pudieron cargar los movimientos.");
      }
    } finally {
      setLoading(false);
    }
  }, [month, year, typeFilter]);

  useEffect(() => {
    load();
  }, [load]);

  // Refresh whenever the global Voice FAB creates a transaction
  useEffect(() => {
    const reload = () => load();
    window.addEventListener("finly:transaction-created", reload);
    return () => window.removeEventListener("finly:transaction-created", reload);
  }, [load]);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/transactions/${id}`);
      toast.success("Movimiento eliminado");
      load();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const handleExportCSV = () => {
    try {
      exportTransactionsCSV(filtered, "MXN");
      toast.success("CSV descargado");
    } catch {
      toast.error("No se pudo generar el CSV");
    }
  };

  const handleExportPDF = async () => {
    try {
      const { data: summary } = await api.get(`/summary?month=${month}&year=${year}`);
      exportTransactionsPDF(filtered, summary, month, year, "MXN");
      toast.success("PDF descargado");
    } catch {
      toast.error("No se pudo generar el PDF");
    }
  };

  const filtered = transactions.filter((t) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      t.category.toLowerCase().includes(s) ||
      (t.note || "").toLowerCase().includes(s)
    );
  });

  const years = [];
  for (let y = now.getFullYear() - 3; y <= now.getFullYear() + 1; y++) years.push(y);
  const months = [
    { value: 1, label: "Ene" }, { value: 2, label: "Feb" }, { value: 3, label: "Mar" },
    { value: 4, label: "Abr" }, { value: 5, label: "May" }, { value: 6, label: "Jun" },
    { value: 7, label: "Jul" }, { value: 8, label: "Ago" }, { value: 9, label: "Sep" },
    { value: 10, label: "Oct" }, { value: 11, label: "Nov" }, { value: 12, label: "Dic" },
  ];

  return (
    <Layout>
      <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4 fade-up">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#94A3B8] font-medium mb-2">
            Historial · {monthLabel(month, year)}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#F1F5F9]">
            Transacciones
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={handleExportCSV}
            disabled={filtered.length === 0}
            className="bg-[#131C2E] border-[#2A3447] text-[#F1F5F9] hover:bg-[#1B2638]"
            data-testid="export-csv-btn"
          >
            <FileDown className="w-4 h-4 mr-2" />
            CSV
          </Button>
          <Button
            variant="outline"
            onClick={handleExportPDF}
            disabled={filtered.length === 0}
            className="bg-[#131C2E] border-[#2A3447] text-[#F1F5F9] hover:bg-[#1B2638]"
            data-testid="export-pdf-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            PDF
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              if (!isPremium) {
                setUpgradeOpen(true);
                return;
              }
              setOcrOpen(true);
            }}
            className="bg-[#131C2E] border-[#2A3447] text-[#F1F5F9] hover:bg-[#1B2638]"
            data-testid="open-ocr-entry-btn"
          >
            <ScanLine className="w-4 h-4 mr-2" />
            Escanear recibo
            {!isPremium && (
              <span className="ml-2 text-[10px] uppercase tracking-wider bg-[#0052FF] text-white px-1.5 py-0.5 rounded">
                Pro
              </span>
            )}
          </Button>
          <QuickEntryDialog categories={categories} onCreated={load} />
        </div>
      </div>

      <div className="clay-card p-5 mb-6 fade-up" data-testid="tx-filters">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="bg-[#131C2E] border-[#2A3447]" data-testid="tx-month-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#131C2E]">
              {months.map((m) => (
                <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="bg-[#131C2E] border-[#2A3447]" data-testid="tx-year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#131C2E]">
              {years.map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="bg-[#131C2E] border-[#2A3447]" data-testid="tx-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#131C2E]">
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="income">Ingresos</SelectItem>
              <SelectItem value="expense">Gastos</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar..."
              className="pl-9 bg-[#131C2E] border-[#2A3447]"
              data-testid="tx-search-input"
            />
          </div>
        </div>
      </div>

      <div className="clay-card overflow-hidden fade-up" data-testid="tx-table">
        {loading ? (
          <p className="text-sm text-[#94A3B8] py-12 text-center">Cargando...</p>
        ) : filtered.length === 0 ? (
          transactions.length === 0 ? (
            <div
              className="py-10 px-4 sm:px-8 text-center flex flex-col items-center gap-5"
              data-testid="empty-transactions-page-state"
            >
              <div className="w-14 h-14 rounded-full bg-[#0052FF]/10 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-[#0052FF]" />
              </div>
              <div className="space-y-2 max-w-md">
                <h3 className="font-display text-xl font-semibold text-[#F1F5F9]">
                  Aún no tienes movimientos en {monthLabel(month, year)}
                </h3>
                <p className="text-sm text-[#64748B] leading-relaxed">
                  Aquí verás cada ingreso o gasto que registres. Empieza con cualquiera de estas opciones:
                </p>
              </div>
              <ol className="text-left text-sm text-[#F1F5F9] space-y-2.5 max-w-md w-full">
                <li className="flex gap-3 items-start">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#0052FF] text-white text-xs font-semibold flex items-center justify-center">1</span>
                  <span>
                    Pulsa <strong className="font-semibold">Nuevo movimiento</strong> arriba y captura monto, categoría y fecha.
                  </span>
                </li>
                <li className="flex gap-3 items-start">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#EF4444] text-white text-xs font-semibold flex items-center justify-center">2</span>
                  <span>
                    Toca el <strong className="font-semibold">botón coral 🎤</strong> en la esquina y dicta tu gasto en lenguaje natural.
                  </span>
                </li>
                <li className="flex gap-3 items-start">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#10B981] text-white text-xs font-semibold flex items-center justify-center">3</span>
                  <span>
                    O prueba <strong className="font-semibold">Escanear recibo</strong> {!isPremium && <em className="text-[#0052FF]">(Pro)</em>} para que Finly extraiga los datos por ti.
                  </span>
                </li>
              </ol>
              <p className="text-xs text-[#94A3B8] pt-2 max-w-md">
                Tip: una vez registres tus primeros movimientos, podrás filtrarlos por mes, tipo o buscar por texto.
              </p>
            </div>
          ) : (
            <p className="text-sm text-[#94A3B8] py-12 text-center">
              No hay movimientos que coincidan con tus filtros.
            </p>
          )
        ) : (
          <div className="divide-y divide-[#2A3447]">
            {/* Header */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-[#1B2638] text-xs uppercase tracking-[0.2em] text-[#94A3B8] font-medium">
              <div className="col-span-2">Fecha</div>
              <div className="col-span-3">Categoría</div>
              <div className="col-span-2">Cuenta</div>
              <div className="col-span-2">Nota</div>
              <div className="col-span-2 text-right">Monto</div>
              <div className="col-span-1"></div>
            </div>
            {filtered.map((t) => {
              const acct = accounts.find((a) => a.id === t.account_id);
              return (
              <div
                key={t.id}
                className="grid grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-[#1B2638]"
                data-testid={`tx-item-${t.id}`}
              >
                <div className="col-span-12 md:col-span-2 flex items-center gap-2">
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: t.type === "income" ? "rgba(0,201,125,0.18)" : "rgba(239,68,68,0.18)" }}
                  >
                    {t.type === "income" ? (
                      <TrendingUp className="w-3.5 h-3.5 text-[#00C97D]" />
                    ) : (
                      <TrendingDown className="w-3.5 h-3.5 text-[#EF4444]" />
                    )}
                  </div>
                  <span className="text-sm font-numeric text-[#F1F5F9]">{formatDate(t.date)}</span>
                </div>
                <div className="col-span-6 md:col-span-3 text-sm font-medium text-[#F1F5F9]">
                  {t.category}
                </div>
                <div className="col-span-6 md:col-span-2 text-xs" data-testid={`tx-account-${t.id}`}>
                  {acct ? (
                    <span
                      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border"
                      style={{ borderColor: acct.color || "#2A3447", color: acct.color || "#94A3B8", backgroundColor: "rgba(255,255,255,0.04)" }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: acct.color || "#94A3B8" }} />
                      {acct.name}
                    </span>
                  ) : (
                    <span className="text-[#64748B]">—</span>
                  )}
                </div>
                <div className="col-span-6 md:col-span-2 text-sm text-[#94A3B8] truncate">
                  {t.note || "—"}
                </div>
                <div
                  className="col-span-9 md:col-span-2 text-right font-numeric font-semibold text-sm"
                  style={{ color: t.type === "income" ? "#00C97D" : "#EF4444" }}
                >
                  {t.type === "income" ? "+" : "-"}
                  {formatCurrency(t.amount, "MXN")}
                </div>
                <div className="col-span-3 md:col-span-1 flex justify-end">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#EF4444]/15"
                        data-testid={`delete-tx-${t.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-[#131C2E] border border-[#2A3447] text-[#F1F5F9]">
                      <AlertDialogHeader>
                        <AlertDialogTitle>¿Eliminar movimiento?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Esta acción no se puede deshacer.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDelete(t.id)}
                          className="bg-[#EF4444] hover:bg-[#DC2626]"
                          data-testid={`confirm-delete-${t.id}`}
                        >
                          Eliminar
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
      <OcrEntryDialog
        open={ocrOpen}
        onOpenChange={setOcrOpen}
        categories={categories}
        onCreated={load}
        isPremium={isPremium}
        onUpgrade={() => {
          setOcrOpen(false);
          setUpgradeOpen(true);
        }}
      />
      <SubscribeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} />
    </Layout>
  );
}
